// client/src/components/shared/health/QuarterlyTrendChart.js
// Linjediagram + statistik för Q4-trend. Hämtar veckodata via /trend och
// renderar en inline MiniLineChart.
import React, { useState, useEffect, useMemo } from 'react';
import API_CONFIG from '../../../config/apiConfig';

const MiniLineChart = ({ data, height = 120 }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        width: '100%', 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        color: '#666',
        fontSize: '12px'
      }}>
        Ingen Q4 data
      </div>
    );
  }
  
  const maxCount = Math.max(...data.map(d => d.count || 0));
  const chartWidth = 200;
  const lineColor = '#2196f3';
  
  return (
    <div style={{ 
      width: '100%', 
      height, 
      padding: '4px 0px', 
      backgroundColor: 'transparent', 
      borderRadius: '4px'
    }}>
      <svg width="100%" height={height - 16} viewBox={`0 0 ${chartWidth} ${height - 16}`}>
        {[0, 50, 100].map(percent => (
          <line 
            key={percent}
            x1="30" 
            y1={((height - 40) * (100 - percent)) / 100 + 10}
            x2={chartWidth - 10} 
            y2={((height - 40) * (100 - percent)) / 100 + 10}
            stroke="#e0e0e0" 
            strokeDasharray="2,2"
          />
        ))}
        
        <polyline
          fill="none"
          stroke={lineColor}
          strokeWidth="2"
          points={data.map((item, index) => {
            const x = 30 + (index * (chartWidth - 40)) / Math.max(data.length - 1, 1);
            const y = maxCount > 0 
              ? ((height - 40) * (maxCount - (item.count || 0))) / maxCount + 10
              : height - 30;
            return `${x},${y}`;
          }).join(' ')}
        />
        
        {data.map((item, index) => {
          const x = 30 + (index * (chartWidth - 40)) / Math.max(data.length - 1, 1);
          const y = maxCount > 0 
            ? ((height - 40) * (maxCount - (item.count || 0))) / maxCount + 10
            : height - 30;
          
          const hasData = item.count > 0;
          
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r={hasData ? "3" : "1"}
              fill={hasData ? lineColor : "#ccc"}
              stroke="white"
              strokeWidth={hasData ? "1" : "0"}
              opacity={hasData ? 1 : 0.4}
            />
          );
        })}
        
        <text x="25" y="15" textAnchor="end" fontSize="10" fill="#666">{maxCount}</text>
        <text x="25" y={height - 25} textAnchor="end" fontSize="10" fill="#666">0</text>
        
        <line x1="30" y1="10" x2="30" y2={height - 20} stroke="#666" strokeWidth="1" />
        <line x1="30" y1={height - 20} x2={chartWidth - 10} y2={height - 20} stroke="#666" strokeWidth="1" />
      </svg>
    </div>
  );
};

const QuarterlyTrendChart = ({ 
  symptoms, 
  level, 
  postalCode, 
  city, 
  showSimilar = false
}) => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executionTime, setExecutionTime] = useState(0);
  
  const Q4_WEEKS = useMemo(() => 
    Array.from({length: 14}, (_, i) => i + 40), 
  []);
  
  useEffect(() => {
    if (!symptoms || symptoms.length === 0) return;
    
    const timer = setTimeout(() => {
      fetchOptimizedQ4TrendData();
    }, 500);
    
    return () => clearTimeout(timer);
    
  }, [symptoms, level, postalCode, city, Q4_WEEKS]);
  
  const fetchOptimizedQ4TrendData = async () => {
    setLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      const symptomsParam = Array.isArray(symptoms)
        ? symptoms.join(',')
        : symptoms;

      let url = `/health-data/trend?startWeek=40&endWeek=53&symptoms=${encodeURIComponent(symptomsParam)}&scope=${level}`;

      if (level === 'postal' && postalCode) {
        url += `&postalCode=${encodeURIComponent(postalCode.replace(/\s+/g, ''))}`;
      }
      if ((level === 'city' || level === 'postal') && city) {
        url += `&city=${encodeURIComponent(city)}`;
      }

      const fullUrl = `${API_CONFIG.baseUrl}${url}`;

      const response = await fetch(fullUrl);
      const result = await response.json();
      
      if (result.success && result.data) {
        // /trend returnerar { week, matchingReports, totalReports }.
        // count-fallbacken behålls för bakåtkompatibilitet om någon annan
        // endpoint återanvänder den här komponenten med annan shape.
        const chartData = result.data.map(weekData => {
          const matches = weekData.matchingReports ?? weekData.count ?? 0;
          return {
            week: weekData.week,
            count: matches,
            hasData: matches > 0,
            isTargetWeek: Q4_WEEKS.includes(weekData.week)
          };
        });
        
        setTrendData(chartData);
        setExecutionTime(Date.now() - startTime);
        
      } else {
        throw new Error(result.error || 'Trend API failed');
      }
      
    } catch (error) {
      console.error(`Error in Q4 fetch for ${level}:`, error);
      setError(error.message);
      setExecutionTime(Date.now() - startTime);
      
      setTrendData(Q4_WEEKS.map(week => ({
        week,
        count: 0,
        hasData: false,
        isTargetWeek: true
      })));
    } finally {
      setLoading(false);
    }
  };
  
  const totalReports = trendData.reduce((sum, item) => sum + item.count, 0);
  const weeksWithData = trendData.filter(item => item.hasData).length;
  const peakWeek = trendData.reduce((max, item) => 
    item.count > max.count ? item : max, 
    { week: 0, count: 0 }
  );
  
  if (loading) {
    return (
      <div style={{ 
        padding: '15px', 
        textAlign: 'center',
        backgroundColor: '#f9f9f9',
        borderRadius: '4px',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{ fontSize: '12px', color: '#666' }}>
          🔄 Hämtar Q4 trend data...
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ 
        padding: '15px', 
        textAlign: 'center',
        backgroundColor: '#fff3cd',
        borderRadius: '4px',
        border: '1px solid #ffeaa7'
      }}>
        <div style={{ fontSize: '12px', color: '#856404' }}>
          ⚠️ Trend data misslyckades
        </div>
        <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="quarterly-trend-chart">
      {/* ✅ REMOVED: Header med getLevelDisplayName() */}
      
      {/* ✅ REMOVED: Symptom display */}
      
      {/* Chart */}
      <MiniLineChart data={trendData} height={120} />
      
      {/* Summary */}
      <div style={{ 
        marginTop: '8px',
        fontSize: '11px',
        color: '#666',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <span>Total: {totalReports}</span>
        <span>Data: {weeksWithData}/{Q4_WEEKS.length} veckor</span>
        {peakWeek.count > 0 && (
          <span>Peak: V{peakWeek.week}</span>
        )}
      </div>
      
      {/* ✅ REMOVED: "Symptom-trend Q4 2024" description */}
    </div>
  );
};

export default QuarterlyTrendChart;